import { ChangeEvent, FunctionComponent, useEffect, useState } from 'react'
import styles from './familiesList.module.scss'
import FamiliesHeader from './header/familiesHeader'
import { DaycareType } from '@/types/Daycare'
import { Navigate, NavigateFunction, useLocation, useNavigate } from 'react-router-dom'
import { Parent } from '@/types/Parent'
import { useQuery } from '@apollo/client'
import { GET_CHILD_LIST, GET_PARENT_LIST } from '@/modules/API/parent/parentHandler'
import Loading from '@/components/Loading/Loading'
import FamiliesTableHandler from './FamiliesTableHandler/familiesTableHandler'
import moment from 'moment/moment'
import { getTextFromLanguage } from '@/modules/helper/languageHelper'
import { useAuth } from '@/modules/context/AuthContext'
import ChildsTableHandler from './ChildsTableHandler/childsTableHandler'
import { ChildDaycareWithParents, ChildWithParents } from '@/types/Child'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { useTranslation } from 'react-i18next'
import { GET_FAMILY_CONSENT_REQUESTS, GET_OUTGOING_CONSENT_REQUESTS } from '@/modules/API/parent/familyConsentHandler'
import ConsentPage from '../consent/ConsentPage'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { NotificationsActive } from '@mui/icons-material'

const FamiliesList: FunctionComponent = () => {
    const { user } = useAuth()

    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const [parentsList, setParentsList] = useState<Array<Parent>>([])
    const [childsList, setChildsList] = useState<Array<ChildWithParents>>([])

    const isRpeOrLaep: boolean =
        user?.currentDaycare?.type === DaycareType.rpe || user?.currentDaycare?.type === DaycareType.laep

    const { pathname } = useLocation()

    const [switchTab, setSwitchTab] = useState<number>(isRpeOrLaep || pathname === '/adults' ? 1 : 0)

    function handleTabSwitch(_e: ChangeEvent<NonNullable<unknown>>, newTab: number): void {
        setSwitchTab(newTab)
        navigate(switchTab === 0 ? '/adults' : '/childs')
    }

    const {
        loading: loadingParentList,
        data: dataParentList,
        error: errorParentList,
        refetch: refetchParentList
    } = useQuery(GET_PARENT_LIST, {
        variables: {
            daycare_id: Number(user?.currentDaycare?.id)
        },
        fetchPolicy: 'network-only'
    })

    useEffect(() => {
        if (dataParentList && dataParentList.getParentList) {
            setParentsList(dataParentList.getParentList)
        }
    }, [dataParentList])

    async function refetchAllParentList(): Promise<void> {
        const [{ data }] = await Promise.all([refetchParentList(), refetchConsentData(), refetchOutgoingConsentData()])
        if (data) {
            setParentsList(data.getParentList)
        }
    }

    const {
        loading: loadingChildList,
        data: dataChildList,
        error: errorChildList,
        refetch: refetchChildList
    } = useQuery(GET_CHILD_LIST, {
        variables: {
            daycareId: Number(user?.currentDaycare?.id)
        },
        fetchPolicy: 'network-only'
    })

    useEffect(() => {
        if (dataChildList && dataChildList.getChildList) {
            setChildsList(dataChildList.getChildList)
        }
    }, [dataChildList])

    async function refetchAllChildList(): Promise<void> {
        const [{ data }] = await Promise.all([refetchChildList(), refetchConsentData(), refetchOutgoingConsentData()])
        if (data) {
            setChildsList(data.getChildList)
        }
    }

    const [loadingGetAllParentsInAllDaycares, setLoadingGetAllParentsInAllDaycares] = useState<boolean>(false)

    const [showConsentPage, setShowConsentPage] = useState<boolean>(false)

    const { data: consentData, refetch: refetchConsentData } = useQuery(GET_FAMILY_CONSENT_REQUESTS, {
        variables: { daycareId: Number(user?.currentDaycare?.id) },
        skip: !user?.currentDaycareRights.canWriteFamilies
    })

    const { data: outgoingConsentData, refetch: refetchOutgoingConsentData } = useQuery(GET_OUTGOING_CONSENT_REQUESTS, {
        variables: { daycareId: Number(user?.currentDaycare?.id) },
        skip: !user?.currentDaycareRights.canReadFamilies
    })

    const [incomingConsentCount, setIncomingConsentCount] = useState<number>(0)
    const [outgoingConsentCount, setOutgoingConsentCount] = useState<number>(0)

    useEffect(() => {
        setIncomingConsentCount(consentData?.getFamilyConsentRequests?.length ?? 0)
    }, [consentData])

    useEffect(() => {
        setOutgoingConsentCount(outgoingConsentData?.getOutgoingConsentRequests?.length ?? 0)
    }, [outgoingConsentData])

    async function getAllParentsInAllDaycares(showOnlyWithoutDaycare: boolean): Promise<void> {
        setLoadingGetAllParentsInAllDaycares(true)
        const { data } = await refetchParentList({
            daycare_id: undefined
        })
        if (data) {
            if (showOnlyWithoutDaycare) {
                setParentsList(data.getParentList.filter((parent: Parent) => parent.daycares.length === 0))
            } else {
                setParentsList(data.getParentList)
            }
        }
        setLoadingGetAllParentsInAllDaycares(false)
    }

    useEffect(() => {
        moment.locale(getTextFromLanguage())
    }, [])

    if (showConsentPage) {
        return <ConsentPage onClose={() => setShowConsentPage(false)} />
    }

    return (
        <div className={styles.PagePosition}>
            {user && user.currentDaycare ? (
                <>
                    <div className={styles.Header}>
                        <FamiliesHeader
                            currentDaycare={user.currentDaycare}
                            switchTab={switchTab}
                            handleTabSwitch={handleTabSwitch}
                            refetchAllParent={refetchAllParentList}
                            refetchAllChilds={refetchAllChildList}
                            getAllParentsInAllDaycares={getAllParentsInAllDaycares}
                            loadingGetAllParentsInAllDaycares={loadingGetAllParentsInAllDaycares}
                        />
                    </div>
                    {(incomingConsentCount > 0 || outgoingConsentCount > 0) && (
                        <div className={styles.SubHeader}>
                            <ButtonTextIcon
                                type='custom'
                                customIcon={<NotificationsActive />}
                                customText={t('consentRequests')}
                                badge={incomingConsentCount + outgoingConsentCount}
                                onClick={() => setShowConsentPage(true)}
                            />
                        </div>
                    )}
                    {switchTab === 1 && user?.currentDaycareRights.canReadFamilies ? (
                        <>
                            {loadingParentList && <Loading overall={false} />}
                            {dataParentList && (
                                <FamiliesTableHandler
                                    families={parentsList}
                                    currentDaycare={user.currentDaycare}
                                    refetchAllParent={refetchAllParentList}
                                />
                            )}
                            {errorParentList && <ErrorMessage error={t('generalError')} />}
                        </>
                    ) : user?.currentDaycareRights.canReadChild ? (
                        <>
                            {loadingChildList && <Loading overall={false} />}
                            {dataChildList && (
                                <ChildsTableHandler
                                    childs={childsList.filter(
                                        (child: ChildWithParents) =>
                                            child.daycares.find(
                                                (daycare: ChildDaycareWithParents): boolean =>
                                                    daycare.daycareId === user.currentDaycare?.id
                                            )?.visible
                                    )}
                                    currentDaycare={user.currentDaycare}
                                    canWriteRegular={user.currentDaycareRights.canWriteContracts}
                                    canReadRegular={user.currentDaycareRights.canReadContracts}
                                    isRpeOrLaep={isRpeOrLaep}
                                    refetchAllParent={refetchAllParentList}
                                    refetchAllChilds={refetchAllChildList}
                                />
                            )}
                            {errorChildList && <ErrorMessage error={t('generalError')} />}
                        </>
                    ) : (
                        <Navigate to={'/calendar'} />
                    )}
                    {loadingGetAllParentsInAllDaycares && <Loading overall={true} />}
                </>
            ) : (
                <Navigate to='/' replace />
            )}
        </div>
    )
}

export default FamiliesList
